<template>
  <div class="page-container">
    <div class="list-page" :class="{ 'locale-zh-CN': isZhLocale }">
      <PageHeader :config="config" @module-click="handleModuleClick" />
      <Breadcrumb :items="breadcrumbItems" />

      <SearchBanner
        search-object="serviceProduct"
        :title-main1="headerConfig.titleMain1"
        :title-main2="headerConfig.titleMain2"
        :subtitle="headerConfig.titleSub"
        :placeholder="headerConfig.searchPlaceholder"
        :button-text="headerConfig.searchButton"
        :keyword.sync="searchKeyword"
        :background-image="bannerBg"
        @search="handleSearch"
      />

      <div class="filter-panel content-wrap">
        <div class="filter-bar">
          <img class="filter-ico" :src="iconType" alt="" @error="onImgError" />
          <span class="filter-label">
            <span class="filter-label-text">{{ config.typeFilter?.label }}</span>
            <span class="filter-colon">：</span>
          </span>
          <div class="filter-chips">
            <button
              v-for="tab in typeTabs"
              :key="'t-' + (tab.value || 'all')"
              type="button"
              class="filter-chip"
              :class="{ active: activeType === tab.value }"
              @click="handleTypeChange(tab.value)"
            >
              {{ tab.label }}
            </button>
          </div>
        </div>
      </div>

      <div class="list-wrap content-wrap">
        <div v-if="loading" class="section-empty">{{ paginationConfig.loading }}</div>
        <div v-else-if="list.length" class="card-grid">
          <ServiceProductCard
            v-for="item in list"
            :key="item.id"
            :item="item"
            @click="goDetail"
          />
        </div>
        <div v-else class="section-empty">{{ emptyText }}</div>

        <div v-if="totalCount > 0" class="pagination">
          <span class="pagination-info">{{ pageInfoText }}</span>
          <div class="pagination-controls">
            <button class="page-btn" :disabled="currentPage === 1" @click="prevPage">
              {{ paginationConfig.prevPage }}
            </button>
            <button
              v-for="page in visiblePages"
              :key="page"
              type="button"
              class="page-number"
              :class="{ active: currentPage === page }"
              @click="handlePageChange(page)"
            >
              {{ page }}
            </button>
            <button class="page-btn" :disabled="currentPage === totalPages" @click="nextPage">
              {{ paginationConfig.nextPage }}
            </button>
          </div>
        </div>
      </div>

      <PageFooter />
    </div>
  </div>
</template>

<script>
import { applyPageScale, setupPageScaleObserver, teardownPageScaleObserver } from '@/utils/pageScale'
import { getServiceProductNewConfig } from '@/config/serviceProductNewConfig'
import PageHeader from '@/components/PageHeader.vue'
import PageFooter from '@/components/PageFooter.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import SearchBanner from '@/components/SearchBanner.vue'
import ServiceProductCard from '@/components/service/ServiceProductCard.vue'
import serviceApi from '@/api/service'
import supplyDemandApi from '@/api/supplyDemand'
import { getSafeImage, handleImgError } from '@/utils/imageFallback'
import { getText, getCurrentLocale } from '@/utils/i18nText'
import { CATEGORY_ALL_LABEL } from '@/utils/filterTabs'

export default {
  name: 'ServiceProductNew',
  components: {
    PageFooter,
    PageHeader,
    Breadcrumb,
    SearchBanner,
    ServiceProductCard
  },
  data() {
    return {
      config: {},
      searchKeyword: '',
      breadcrumbItems: [],
      typeTabs: [],
      typeDictRows: [],
      activeType: '',
      list: [],
      loading: false,
      currentPage: 1,
      pageSize: 12,
      totalCount: 0
    }
  },
  computed: {
    isZhLocale() {
      return getCurrentLocale() === 'zh-CN'
    },
    emptyText() {
      return getText('common.empty') || '暂无数据'
    },
    headerConfig() {
      return this.config.header || {}
    },
    paginationConfig() {
      return this.config.pagination || {}
    },
    bannerBg() {
      return getSafeImage(this.headerConfig.bannerBg)
    },
    iconType() {
      return getSafeImage(this.config.icons?.type)
    },
    totalPages() {
      const pages = Math.ceil(this.totalCount / this.pageSize)
      return pages > 0 ? pages : 1
    },
    visiblePages() {
      const total = this.totalPages
      const current = this.currentPage
      const max = 6
      if (total <= max) return Array.from({ length: total }, (_, i) => i + 1)
      let start = Math.max(1, current - 2)
      let end = start + max - 1
      if (end > total) {
        end = total
        start = end - max + 1
      }
      return Array.from({ length: end - start + 1 }, (_, i) => start + i)
    },
    pageInfoText() {
      const tpl = this.paginationConfig.pageInfo || '共{total}条，每页显示{pageSize}条'
      return tpl.replace('{total}', this.totalCount).replace('{pageSize}', this.pageSize)
    }
  },
  created() {
    this.loadConfig()
  },
  mounted() {
    this.$nextTick(() => {
      this.setScale()
      this.observePageResize()
    })
    window.addEventListener('resize', this.setScale)
  },
  beforeDestroy() {
    teardownPageScaleObserver(this)
    window.removeEventListener('resize', this.setScale)
  },
  methods: {
    setScale() {
      applyPageScale(this.getPageScaleEl())
    },
    getPageScaleEl() {
      return this.$el.querySelector('.list-page')
    },
    observePageResize() {
      setupPageScaleObserver(this, () => this.getPageScaleEl())
    },
    onImgError(e) {
      handleImgError(e)
    },
    async loadConfig() {
      this.config = getServiceProductNewConfig(getCurrentLocale())
      const bc = this.config.breadcrumb || {}
      this.breadcrumbItems = [
        { label: bc.home || '', path: '/index' },
        { label: bc.parent || '', path: bc.parentPath || '/innovation-service' },
        { label: bc.current || '', path: '' }
      ]
      const kw = this.$route?.query?.keyword
      if (kw) this.searchKeyword = String(kw)
      await this.loadTypeDict()
      this.loadList()
    },
    parseDictRows(result) {
      if (!result || result.status !== 0) return []
      if (result.data && Array.isArray(result.data.rows)) return result.data.rows
      if (Array.isArray(result.data)) return result.data
      if (typeof result.data === 'object' && result.data !== null) {
        return Object.values(result.data).flat()
      }
      return []
    },
    async loadTypeDict() {
      const allLabel = this.config.typeFilter?.allLabel || CATEGORY_ALL_LABEL
      try {
        const res = await supplyDemandApi.getDictData(['yOrgProductAllType'])
        const rows = this.parseDictRows(res.data || {})
        this.typeDictRows = rows.filter(item => item && item.dictCode === 'yOrgProductAllType')
        const firstLevel = this.typeDictRows
          .filter(
            item =>
              item.parentCode === null || item.parentCode === '' || item.parentCode === undefined
          )
          .slice()
          .sort((a, b) => (a.sortNum || 0) - (b.sortNum || 0))
        const unique = new Map()
        firstLevel.forEach(item => {
          unique.set(String(item.itemCode), item)
        })
        this.typeTabs = [
          { label: allLabel, value: '' },
          ...Array.from(unique.values()).map(item => ({
            label: item.itemValue || item.itemCode || '',
            value: String(item.itemCode || '')
          }))
        ]
      } catch (e) {
        console.error('加载服务商品类型字典失败:', e)
        this.typeTabs = [{ label: allLabel, value: '' }]
        this.typeDictRows = []
      }
      this.activeType = this.typeTabs[0]?.value ?? ''
    },
    buildFeatures(item) {
      const type = item.type || ''
      const lines = []
      if (type === '银行' || type === '担保') {
        if (item.interestRate) lines.push(String(item.interestRate))
        if (item.interestLimit) lines.push(String(item.interestLimit))
      } else if (type === '保险') {
        if (item.insureRate) lines.push(String(item.insureRate))
        if (item.insureLimit) lines.push(String(item.insureLimit))
      } else if (type === '基金') {
        if (item.fundScale) lines.push(String(item.fundScale))
        if (item.fundLimit) lines.push(String(item.fundLimit))
      } else if (type === '服务商品') {
        if (item.productPrice) lines.push(`价格${item.productPrice}`)
        if (item.productDesc) lines.push(String(item.productDesc))
        else if (item.productIntro) lines.push(String(item.productIntro))
      }
      if (!lines.length) {
        if (item.productAdvantage) lines.push(String(item.productAdvantage))
        if (item.productDesc) lines.push(String(item.productDesc))
        if (item.serviceContent) lines.push(String(item.serviceContent))
      }
      return lines
        .map(text => String(text || '').replace(/<[^>]+>/g, '').trim())
        .filter(Boolean)
        .slice(0, 2)
    },
    mapProductItem(item) {
      let img = ''
      if (item.productCover) {
        if (String(item.productCover).startsWith('http')) {
          img = item.productCover
        } else {
          img = serviceApi.getImageUrl(item.productCover) || ''
        }
      }
      return {
        id: item.id,
        title: item.productName || '',
        category: item.productTypeName || '',
        tag: item.type || item.productTypeName || '',
        features: this.buildFeatures(item),
        image: img,
        provider: item.orgName || '',
        rawData: item
      }
    },
    async loadList() {
      this.loading = true
      try {
        const res = await serviceApi.getProductList({
          pageNum: this.currentPage,
          pageSize: this.pageSize,
          keyword: this.searchKeyword || '',
          productType: this.activeType || '',
          provinceId: ''
        })
        const result = res.data || {}
        if (result.status === 0 && result.data) {
          const data = result.data
          const rows = data.rows || data.list || []
          this.list = rows.map(item => this.mapProductItem(item))
          this.totalCount = Number(data.totalCount || data.total || 0) || 0
        } else {
          this.list = []
          this.totalCount = 0
        }
      } catch (e) {
        console.error('加载服务商品列表失败:', e)
        this.list = []
        this.totalCount = 0
      } finally {
        this.loading = false
      }
    },
    handleTypeChange(value) {
      this.activeType = value
      this.currentPage = 1
      this.loadList()
    },
    handleSearch() {
      this.currentPage = 1
      this.loadList()
    },
    handleModuleClick() {},
    goDetail(item) {
      const raw = item?.rawData || item || {}
      const id = raw.id || item?.id || ''
      const cacheKey = `product_detail_${id}`
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify(raw))
      } catch (e) {
        // ignore
      }
      this.$router.push({
        path: '/service-product/detail',
        query: { id, cacheKey }
      })
    },
    handlePageChange(page) {
      this.currentPage = page
      this.loadList()
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.loadList()
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.loadList()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  background: #fff;
}

.list-page {
  width: 1920px;
  min-height: 100vh;
  background: #fff;
  transform-origin: top left;
}

.content-wrap {
  width: 1700px;
  margin-left: 110px;
  margin-right: auto;
  box-sizing: border-box;
}

::v-deep .header-banner {
  margin-bottom: 0;
}

.filter-panel {
  margin-top: 0;
  margin-bottom: 18px;
}

.filter-bar {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 24px;
  background: rgba(160, 144, 60, 0.1);
  box-sizing: border-box;
}

.filter-ico {
  width: 16px;
  height: 16px;
  margin-top: 6px;
  flex-shrink: 0;
  object-fit: contain;
}

.filter-label {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  width: 3em;
  margin-top: 4px;
  font-size: 15px;
  font-weight: 600;
  color: #a0903c;
  line-height: 1.4;
  box-sizing: border-box;
}

.filter-label-text {
  flex: 1;
  min-width: 0;
  height: 1.4em;
  text-align: justify;
  text-align-last: justify;
  overflow: hidden;
}

.filter-colon {
  flex-shrink: 0;
}

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
  gap: 8px 12px;
  text-align: left;
}

.filter-chip {
  height: 28px;
  padding: 0 14px;
  border: 1px solid transparent;
  border-radius: 2px;
  background: transparent;
  font-size: 14px;
  color: #555;
  cursor: pointer;
  line-height: 26px;
  box-sizing: border-box;

  &.active {
    background: rgba(138, 122, 40, 0.15);
    border: 1px solid rgba(160, 144, 60, 0.55);
    color: #8a7a28;
    font-weight: 600;
  }

  &:hover:not(.active) {
    color: #8c7c26;
  }
}

.list-wrap {
  margin-bottom: 40px;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
  width: 100%;
}

.section-empty {
  width: 100%;
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 16px;
  background: #fff;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 28px;
}

.pagination-info {
  font-size: 14px;
  color: #666;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-btn,
.page-number {
  min-width: 36px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #ddd;
  background: #fff;
  color: #666;
  font-size: 13px;
  cursor: pointer;
  box-sizing: border-box;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &.active,
  &:hover:not(:disabled) {
    border-color: #a0903c;
    color: #8a7a28;
    background: rgba(160, 144, 60, 0.12);
  }
}


.locale-zh-CN {
  .filter-label,
  .field-label,
  .region-label {
    font-size: 16px;
  }

  .filter-chip,
  .field-chip,
  .region-chip {
    font-size: 18px;
    height: 36px;
    line-height: 34px;
    padding: 0 20px;
  }

  .filter-ico,
  .field-ico,
  .region-ico {
    width: 18px;
    height: 18px;
  }

  .pagination-info {
    font-size: 15px;
  }

  .page-btn,
  .page-number {
    min-width: 40px;
    height: 36px;
    font-size: 14px;
  }
}
</style>
